import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - functions
import { pathHomePage, WEB_SERVICE_MIMS, applications } from "../../config/constants";
import { renderAvatar } from "../../config/functions";
import { allowGlobalAdherenceView, getApplicationPath, applicationsData } from "../../config/validateApplication";
import { isAISNProjectHospital } from "../../config/projectFunctions";
// Components
import {
  PatientMainDiv,
  PatientDiv,
  UserDiv,
  AvatarLink,
  AvatarImg,
  UserInfoDiv,
  UserText,
  ShowAppsButton,
  ShowAppsImg,
  ProfileDiv,
  ProfileLink,
  ProfileImg,
  ProfileText,
  ApplicationDiv,
  ApplicationProfileLink,
  ApplicationProfileImg,
  ApplicationLink,
  ApplicationImg,
  ApplicationText,
  ApplicationProfileText,
} from "./styles";
import Axios from "../../config/axios";
import Loader from "../loader";
// Assets
import IcoProfile from "../../assets/icons/ico_patient_profile.svg";
import IcoProfileGray from "../../assets/icons/ico_patient_profile_gray.svg";
import IcoArrowUp from "../../assets/icons/ico_arrow_up.svg";
import IcoArrowDown from "../../assets/icons/ico_arrow_down.svg";
import IcoRecsys from "../../assets/icons/recsys.svg";
import IcoRecsysGray from "../../assets/icons/recsys_gray.svg";

const PatientNavigation = ({ patientId, section, rechargeComponent }) => {
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(true);
  const [showApps, setShowApps] = useState(false);
  const [user, setUser] = useState("");
  const [patientApplications, setPatientApplications] = useState([]);
  const [isThereSomePrescription, setIsThereSomePrescription] = useState(false);
  const [isTherePrescriptionsAfter7Days, setIsTherePrescriptionsAfter7Days] = useState(false);
  const [adherence, setAdherence] = useState(0);
  const [hospitalApplications, setHospitalApplications] = useState([]);
  const clinicianId = useSelector(({ clinician }) => clinician.data.CLINICIAN_ID);
  const clinicianToken = useSelector(({ clinician }) => clinician.data.token);
  const hospitalId = useSelector(({ clinician }) => clinician.data.HOSPITAL_ID);
  const hospitalName = useSelector(({ clinician }) => clinician.data.HOSPITAL_NAME);
  const [isAISNHospital, setIsAISNHospital] = useState(false);
  const [t] = useTranslation("global");

  // Fetch hospital applications
  useEffect(() => {
    if (hospitalId) {
      Axios("get", `${WEB_SERVICE_MIMS}/hospitals/software/${hospitalId}`)
        .then(({ data }) => {
          setHospitalApplications(
            data.filter(
              (application) => application.SOFTWARE_ID !== applications.home.id && application.SOFTWARE_ID !== applications.web.id
            )
          );
        })
        .catch((error) => {
          console.log("Get hospital software: ", error);
        });
    }

    return () => {
      setHospitalApplications([]);
    };
  }, [hospitalId, hospitalName, isAISNHospital]);

  // Fetch patient data
  useEffect(() => {
    if (clinicianToken) {
      Axios("get", `${WEB_SERVICE_MIMS}/patients/fetch/${patientId}`)
        .then(({ data }) => {
          setUser(data.PATIENT_USER);
          setPatientApplications(data.SOFTWARE_ID);
          setIsThereSomePrescription(data.isThereSomePrescription);
          setIsTherePrescriptionsAfter7Days(data.isTherePrescriptionsAfter7Days);
          setAdherence(data.ADHERENCE);
          setIsLoadingPatientData(false);
        })
        .catch((error) => {
          console.log("Get patient data: ", error);
        });
    }

    return () => {
      setUser("");
      setPatientApplications([]);
      setIsThereSomePrescription(false);
      setIsTherePrescriptionsAfter7Days(false);
      setAdherence(0);
      setIsLoadingPatientData(true);
    };
  }, [patientId, rechargeComponent, clinicianToken]);

  // Set AISN project flag
  useEffect(() => {
    const handleSetAISNProject = async () => {
      const isAISNHospital = await isAISNProjectHospital(hospitalName);
      setIsAISNHospital(isAISNHospital);
    };
    handleSetAISNProject();

    return () => {
      setIsAISNHospital(false);
    };
  }, [hospitalName]);

  const isProfileSection = () => section === "profile";

  const renderAISNSection = () => {
    const RGSwearId = applications.wear.id;
    const path = getApplicationPath(RGSwearId, clinicianToken, patientId);
    const { title, activeImg, availableImg } = applicationsData[RGSwearId] || {};
    const localizedTitle = t(title);
    const isRecsysSection = window.location.pathname.includes("recsys");

    return (
      <ApplicationDiv>
        <ApplicationLink to={path}>
          {applications[section] && applications[section].id === RGSwearId ? (
            <>
              <ApplicationImg src={activeImg} alt={localizedTitle} />
              <ApplicationText>{localizedTitle}</ApplicationText>
            </>
          ) : (
            <ApplicationImg src={availableImg} alt={localizedTitle} />
          )}
        </ApplicationLink>

        <ApplicationLink to={`${pathHomePage}/recsys/${clinicianToken}/${patientId}`} display="flex">
          <ApplicationImg src={isRecsysSection ? IcoRecsysGray : IcoRecsys} alt="ico-recsys" />
          {isRecsysSection && <ApplicationText>AISN</ApplicationText>}
        </ApplicationLink>
      </ApplicationDiv>
    );
  };

  const renderApplications = () => {
    return hospitalApplications.map((application) => {
      const applicationId = application.SOFTWARE_ID;
      const hasTheHospitalThisApplication = patientApplications.includes(applicationId);
      const path = getApplicationPath(applicationId, clinicianToken, patientId);
      const { title, activeImg, availableImg, disableImg } = applicationsData[applicationId] || {};
      const localizedTitle = t(title);

      if (
        (applicationId === applications.app.id && patientApplications.includes(applications.plus.id)) ||
        (applicationId === applications.app.id && !patientApplications.includes(applications.app.id))
      ) {
        return null;
      }
      if (applicationId === applications.plus.id && patientApplications.includes(applications.app.id)) {
        return null;
      }

      return (
        <ApplicationLink key={applicationId} to={path} disabled={!hasTheHospitalThisApplication}>
          {applications[section] && applications[section].id === applicationId ? (
            <>
              <ApplicationImg src={activeImg} alt={localizedTitle} />
              <ApplicationText>{localizedTitle}</ApplicationText>
            </>
          ) : (
            <ApplicationImg src={hasTheHospitalThisApplication ? availableImg : disableImg} alt={localizedTitle} />
          )}
        </ApplicationLink>
      );
    });
  };

  const addPatientActivity = () => {
    const clinicianAndPatientData = {
      CLINICIAN_ID: clinicianId,
      PATIENT_ID: patientId,
    };
    Axios("post", `${WEB_SERVICE_MIMS}/clinicians/patient-activity`, clinicianAndPatientData).catch((error) => {
      console.log("Add patient activity: ", error);
    });
  };

  return (
    <>
      {isLoadingPatientData ? (
        <Loader height={50} />
      ) : (
        <PatientMainDiv>
          <PatientDiv>
            <UserDiv>
              <AvatarLink
                onClick={addPatientActivity}
                to={`${pathHomePage}/global-Patient-Adherence/${clinicianToken}/${patientId}`}
                disabled={!allowGlobalAdherenceView(patientApplications) || isAISNHospital}
              >
                <AvatarImg src={renderAvatar(isThereSomePrescription, isTherePrescriptionsAfter7Days, adherence)} alt="avatar" />
              </AvatarLink>
              <UserInfoDiv>
                <UserText>{user}</UserText>
              </UserInfoDiv>
              <ShowAppsButton onClick={() => setShowApps(!showApps)}>
                <ShowAppsImg src={showApps ? IcoArrowUp : IcoArrowDown} alt="ico-arrow" />
              </ShowAppsButton>
            </UserDiv>
            <ProfileDiv>
              <ProfileLink to={`${pathHomePage}/edit-patient/${clinicianToken}/${patientId}`}>
                <ProfileImg src={isProfileSection() ? IcoProfileGray : IcoProfile} alt="profile" />
                {isProfileSection() && <ProfileText>{t("patientAppCard_profileButton")}</ProfileText>}
              </ProfileLink>
            </ProfileDiv>
            {isAISNHospital ? (
              renderAISNSection()
            ) : (
              <ApplicationDiv show={showApps}>
                <ApplicationProfileLink to={`${pathHomePage}/edit-patient/${clinicianToken}/${patientId}`} display="none">
                  <ApplicationProfileImg src={isProfileSection() ? IcoProfileGray : IcoProfile} alt="profile" />
                  {isProfileSection() && <ApplicationProfileText>{t("patientAppCard_profileButton")}</ApplicationProfileText>}
                </ApplicationProfileLink>
                {renderApplications()}
              </ApplicationDiv>
            )}
          </PatientDiv>
        </PatientMainDiv>
      )}
    </>
  );
};

export default PatientNavigation;
